import React, { useState, Fragment, useEffect, useCallback, useRef, useImperativeHandle, useMemo } from 'react';
import {
  Button as MButton,
  Typography,
  Select,
  MenuItem,
  Icon,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  InputAdornment,
  Tooltip,
  Toolbar,
  Box,
  Autocomplete as MuiAutocomplete,
  Menu,
} from '@mui/material';
import { get, set, map, find, size, noop, flatten, isObject, last, keyBy, unset } from 'global/lib/lodash';
import clsx from 'clsx';
import { Access } from 'global/components/droprealms';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { DropzoneArea } from 'global/components/Dropzone';
import { isValidHex, _size, ensureArray, customSearch, realmsToString, pickFrom } from '../../utils';
import { ChromePicker } from 'react-color';
import { LoadingTools } from '../LoadingIcons';
import RichText from 'mui-rte';
import { draftToHtml, htmlToDraft } from '../../utils/htmlconv';
import DropArea from 'global/components/drop-area';
import Image, { ImageList } from 'global/components/image';
import { isImgOrVid, splitMediaByType } from '../../utils/mediaUtils';
import { useStyles, Symbols } from './styles';
import { ColorSlider } from '../color-slider';
import { EditorState, Modifier } from 'draft-js';
import { ImageSelectBtn } from 'feature/report/AssessmentScore/SystemScore';
import { Button, IconButton } from 'global/components/Button';
import DragSortableList from 'global/components/sortable-list';
import { debounce } from '../../lib/lodash';
import { TradeSelect } from '../trade-select';
import { AutoComplete } from '../Autocomplete';
import { DatePicker } from 'global/components/date-picker';
import { Dropitems, getRealmIcon } from '../droprealms';
import moment from 'moment';
import { Media } from '../Media';
import { useScreen } from '../../styles';
import { uploadImageToPerform } from '../../../redux/actions/imageUploader';
import { FILEID } from '../drop-area';
import { awaitAll } from '../../utils/awaitAll';
import { Text } from '../text';

export const fixWeirdDateIssue = (d, addday = false) => {
  try {
    if (!d) {
      return d;
    }
    const dd = addday ? moment(d).add(1, 'day') : d;
    return moment(`${dd.toISOString().split('T')[0]}T00:00:00.000Z`);
  } catch (err) {
    console.warn(err);
    return d;
  }
};

const modify = (editorState, { content }) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  let nextEditorState = EditorState.createEmpty();

  const nextContentState = Modifier.insertText(contentState, selection, content);
  nextEditorState = EditorState.push(editorState, nextContentState, 'insert-characters');

  return nextEditorState;
};

export const convertJBuilderToObj = (d) => {
  const json = {};

  d.map((k) => {
    const { property, value, id } = k;
    set(json, [property], value);
    if (id) {
      set(json, 'id', id);
    }
  });

  return json;
};

export const convertObjToJbuilder = (obj) => {
  obj = obj || {};

  const values = [];

  Object.keys(obj).map((k) => {
    values.push({ property: k, value: obj[k] });
  });

  return values;
};

const symbols = [
  {
    name: 'cust-degree',
    content: '°',
    icon: <div style={Symbols.icon}>°</div>,
    type: 'callback',
    onClick: function (editorState, name, anchor) {
      return modify(editorState, this);
    },
  },
  {
    name: 'cust-tm',
    content: '™',
    icon: <div style={Symbols.icon}>™</div>,
    type: 'callback',
    onClick: function (editorState, name, anchor) {
      return modify(editorState, this);
    },
  },
  {
    name: 'cust-copy',
    content: '©',
    icon: <div style={Symbols.icon}>©</div>,
    type: 'callback',
    onClick: function (editorState, name, anchor) {
      return modify(editorState, this);
    },
  },
  {
    name: 'cust-rtm',
    content: '®',
    icon: <div style={Symbols.icon}>®</div>,
    type: 'callback',
    onClick: function (editorState, name, anchor) {
      return modify(editorState, this);
    },
  },
];

// --- Repeater item: renders a child Form for each repeater entry ---
const RepeaterItem = ({ FormComponent, item, itemIndex, nestedInputs, disabled, onItemChange, onRemove, itemName, formStyle }) => {
  const onItemChangeRef = useRef(onItemChange);
  onItemChangeRef.current = onItemChange;
  const idxRef = useRef(itemIndex);
  idxRef.current = itemIndex;

  const handleFormState = useCallback((vals) => {
    onItemChangeRef.current(idxRef.current, vals);
  }, []);

  const screens = useMemo(() => [{
    inputs: nestedInputs.map((ni) => ({
      ...ni,
      defaultValue: () => {
        const val = item[ni.id];
        if (val !== undefined) return (ni.type === 'DropArea' && !Array.isArray(val)) ? [].concat(val) : val;
        if (ni.type === 'KeyboardDatePicker') return null;
        if (ni.type === 'DropArea') return [];
        return '';
      },
      props: { ...ni.props, disabled },
    })),
  }], [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ height: 'auto' }}>
      <FormComponent
        showAllScreens
        isNoMarginContainer
        useHeight={false}
        onFormState={handleFormState}
        screens={screens}
        formStyle={formStyle}
      />
    </div>
  );
};

const Form = React.forwardRef((props, ref) => {
  const { showPosition, onSubmit, onFormState, loading, useStaticScreens } = props;
  const [formState, setFormState] = useState({});
  const [subState, setSubState] = useState({});
  const [screenIndex, setScreenIndex] = useState(0);
  const [_screens, setScreens] = useState(props.screens);
  const [collapsed, setCollapsed] = useState({});
  const formref = useRef();
  const latestRef = useRef({ formState, screens: props.screens, screenIndex: 0 });
  const richTextDefaultsRef = useRef({});
  const [state, setState] = useState({ mediaOpen: false });

  useEffect(() => {
    const collap = {};
    props.screens.forEach((s, i) => {
      if (Array.isArray(s)) {
        s.forEach((scr, _i) => {
          set(collap, `${i}.${_i}`, get(scr, 'collapsable.collapsed', false));
        });
      } else {
        set(collap, i, get(s, 'collapsable.collapsed', false));
      }
    });
    setCollapsed(collap);
  }, []);

  const useHeight = get(props, 'useHeight', true);
  const sc = useScreen();
  const classes = useStyles({ useHeight, showPosition, ...sc });

  const screens = useStaticScreens ? _screens : get(props, 'screens', []);
  latestRef.current.screens = screens;
  latestRef.current.screenIndex = screenIndex;

  useEffect(() => {
    if (!props.updateStaticScreens) {
      return;
    }

    const buildInput = (input, i) => {
      if (Array.isArray(input)) {
        return input.map((inp, _i) => buildInput(inp, `${i}.${_i}`));
      }
      const propScreenInputs = get(props, `screens.${i}.inputs`, []) || [];
      const newprops = get(
        propScreenInputs.find((si) => get(si, 'id', '').includes(input.id)),
        'props',
        input.props
      );

      return {
        ...input,
        props: newprops || {},
      };
    };

    const newscreens = [...screens].map((screen, i) => {
      return {
        ...screen,
        inputs: (screen.inputs || []).map((s) => buildInput(s, i)),
      };
    });

    setScreens(newscreens);
  }, props.updateStaticScreens || []);

  const showAllScreens = get(props, 'showAllScreens', false);
  const showNavigation = showAllScreens ? false : get(props, 'showNavigation', true);
  const canSubmit = showAllScreens && get(props, 'canSubmit', false);
  const isNoMarginContainer = get(props, 'isNoMarginContainer', false);

  const updateFS = useCallback(
    debounce((fs) => {
      onFormState && onFormState(fs);
    }, 1),
    []
  );

  const updateState = (id, val, update = true, notify = true) => {
    set(formState, id, val);
    if (requiredErrors.length && requiredErrors.includes(id)) {
      const isEmpty = val === undefined || val === null || val === '' || (isObject(val) && _size(val) === 0);
      if (!isEmpty) {
        setRequiredErrors(prev => prev.filter(errId => errId !== id));
      }
    }
    if (update) {
      setFormState({ ...formState });
    }
    latestRef.current.formState = formState;
    set(ref, 'current.formState', formState);
    if (notify) {
      updateFS(formState);
    }
  };

  const clearState = () => {
    Object.keys(formState).forEach((k) => {
      set(formState, k, undefined);
    });
    setFormState({});
    onFormState && onFormState(formState);
  };

  const addAnotherInput = (input, index) => {
    if (input.onAdd) {
      return input.onAdd();
    }

    const _screens = [...screens];
    const currentInputs = get(_screens, [screenIndex, 'inputs'], []);
    const newIndex = index + 1;

    //insert new input
    currentInputs.splice(newIndex, 0, {
      ...input,
      id: `${input.id}|${newIndex}`,
    });

    //delete items from input that should only be on the last one, which is what we inserted
    set(currentInputs, [index, 'add'], undefined);
    set(_screens, [screenIndex, 'inputs'], currentInputs);
    setScreens(_screens);
  };

  const removeInput = (input, index) => {
    if (input.onRemove) {
      return input.onRemove();
    }

    const _screens = [...screens];
    const currentInputs = get(_screens, [screenIndex, 'inputs'], []);
    const itemToRemove = currentInputs[index];

    //insert new input
    currentInputs.splice(index, 1);
    delete formState[get(itemToRemove, 'id')];

    //delete items from input that should only be on the last one, which is what we inserted
    set(currentInputs, [index - 1, 'add'], get(itemToRemove, 'add'));
    set(_screens, [screenIndex, 'inputs'], currentInputs);
    setScreens(_screens);
  };

  const getInputType = (screen, input, index, sIndex) => {
    if (Array.isArray(input)) {
      return <div className={classes.row}>{input.map((inp, i) => getInputType(screen, inp, i, sIndex))}</div>;
    }

    // if (input.useSource || screen.useSource) {
    //   input = get(props, `screens.${sIndex}.inputs.${index}`, input);
    // }

    const isType = get(input, 'type');
    let value = get(formState, input.id);
    if (value === undefined) {
      if (input.defaultValue && typeof input.defaultValue === 'function') {
        const val = input.defaultValue(formState);
        //this should prevent this from hitting an endless loop if the defaultValue
        //function happens to return undefined
        value = val === undefined ? null : val;
        updateState(input.id, value);
      }
    }

    const { helperText: _helperText, ...inputProps } = get(input, 'props', {});
    const isRequired = input.required || inputProps.required;
    const hasRequiredError = requiredErrors.includes(input.id);
    if (isRequired) {
      if (inputProps.label && !inputProps.label.endsWith('*')) {
        inputProps.label = `${inputProps.label} *`;
      } else if (!inputProps.label && inputProps.placeholder && !inputProps.placeholder.endsWith('*')) {
        inputProps.placeholder = `${inputProps.placeholder} *`;
      }
      unset(inputProps, 'required');
    }
    if (hasRequiredError) {
      inputProps.error = true;
    }
    const helperText = hasRequiredError ? (_helperText || 'This field is required') : _helperText;
    const helperTextType = helperText ? (typeof helperText === 'function' ? helperText(formState) : helperText) : undefined;

    let comp = null;

    const isDisabled = get(inputProps, 'disabled', false);
    const variant = isDisabled ? 'standard' : 'outlined';
    const isLast = index === screen.inputs.length - 1;
    const center = get(input, 'center', false);

    if (isType) {
      if (isType === 'RealmDialog') {
        const continueOnSelect = get(inputProps, 'continueOnSelect', true);
        comp = (
          <div style={{ width: '100%', height: '100%' }}>
            <Access
              key={index}
              onSelectedChange={(sel) => {
                updateState(input.id, sel);
                if (inputProps.singleSelect && continueOnSelect) {
                  setScreenIndex(screenIndex + 1);
                }
              }}
              {...inputProps}
            />
          </div>
        );
      } else if (isType === 'DropRealms') {
        const isSingle = get(inputProps, 'singleSelect', false);
        const getValue = () => {
          if (value && value.name) {
            return value;
          } else if (value) {
            return Object.keys(value).map((k) => get(value, k, {}));
          }
        };
        const getDisplayValue = () => {
          const v = getValue();
          if (v && v.name) {
            return v.name;
          } else if (v && Array.isArray(v)) {
            return realmsToString(v);
          }
        };
        const v = !!value ? getValue() : null;
        comp = (
          <Dropitems
            {...inputProps}
            selected={Array.isArray(value) ? keyBy(value, 'id') : value}
            render={(p) => {
              return (
                  <FormControl fullWidth>
                    <InputLabel>{get(inputProps, 'label')}</InputLabel>
                    <Select
                      disabled={isDisabled}
                      label={get(inputProps, 'label')}
                      value={value}
                      renderValue={() => (
                        <div>
                          {!!value && isSingle && <div style={{ marginRight: 5 }}>{getRealmIcon(v)}</div>}
                          {!!value ? getDisplayValue() || get(inputProps, 'placeholder', 'Builder') : get(inputProps, 'placeholder', 'Builder')}
                        </div>
                      )}
                      open={false}
                      onOpen={(e) => {
                        if (!isDisabled) { p.openModal(true); }
                      }}
                      onChange={() => {}}
                    >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                    </Select>
                    {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
                  </FormControl>
              );
            }}
            onUpdate={(sel) => updateState(input.id, sel)}
          />
        );
      } else if (isType === 'Select') {
        const data = typeof input.data === 'function' ? input.data() : input.data;
        comp = (
          <FormControl variant={variant} className={classes.formControl}>
            {inputProps.label && <InputLabel>{inputProps.label}</InputLabel>}
            <Select
              classes={{
                root: classes.width100,
              }}
              disableUnderline
              value={value || ''}
              onChange={(e) => updateState(input.id, e.target.value)}
              {...inputProps}
            >
              {map(data, (item, i) => {
                const d = input.item ? input.item(item) : item;
                return (
                  <MenuItem key={i} value={get(d, 'value', i)}>
                    {get(d, 'title', '')}
                  </MenuItem>
                );
              })}
              {size(data) === 0 && <MenuItem>No Options</MenuItem>}
            </Select>
            {helperTextType && <FormHelperText error={hasRequiredError}>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'MultiSelect') {
        const data = typeof input.data === 'function' ? input.data() : input.data;

        const isDisabledData = isDisabled ? [''] : [];

        comp = (
          <FormControl variant="filled" className={classes.formControl}>
            {inputProps.label && (<InputLabel>{inputProps.label}</InputLabel>)}
            <div className={classes.formWrap}>
              <Select
                className={classes.width100}
                classes={{
                  root: classes.width100,
                }}
                multiple
                disableUnderline
                value={size(value) ? value : isDisabledData}
                onChange={(e) => {
                  const val = e.target.value.filter((f) => f);
                  if (size(val)) {
                    updateState(input.id, val);
                  }
                }}
                {...inputProps}
              >
                {map(size(data) ? data : isDisabledData, (item, i) => {
                  const d = input.item ? input.item(item) : item;
                  return (
                    <MenuItem key={i} value={get(d, 'value', i)}>
                      {get(d, 'title', '')}
                    </MenuItem>
                  );
                })}
                {size(data) === 0 && <MenuItem>No Options</MenuItem>}
              </Select>
              {value && (
                <div className={classes.multiSelectChipContainer}>
                  {value.map((sel, i) => {
                    const selected = find(data, (item) => {
                      const dd = input.item ? input.item(item) : item;
                      return dd.value === sel ? dd : false;
                    });
                    if (!selected) {
                      //item doens't exist in array so update formstate
                      value.splice(i, 1);
                      updateState(input.id, value);
                    }
                    const item = selected ? (input.item ? input.item(selected) : selected) : null;
                    return (
                      <div key={i} className={classes.multiSelectChipWrap}>
                        <Chip
                          key={sel}
                          size={'small'}
                          label={get(item, 'title', '')}
                          className={classes.multiSelectChip}
                          onDelete={
                            isDisabled
                              ? undefined
                              : () => {
                                  value.splice(i, 1);
                                  updateState(input.id, value);
                                }
                          }
                          {...get(input, 'chipProps', {})}
                        />
                        {input.chipTextAdd && <Typography variant={'caption'}>{input.chipTextAdd}</Typography>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {helperTextType && <FormHelperText error={hasRequiredError}>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'UserSelect') {
        const liveData = get(get(props, 'screens', []), [sIndex, 'inputs', index, 'data']);
        const inpData = liveData || input.data;
        const data = typeof inpData === 'function' ? inpData() : inpData;

        const sectoredUsers = (data || [])
          .filter((d) => !!get(d, 'contentRealm.closestRealm'))
          .reduce((a, item) => set(a, get(item, 'contentRealm.distance'), [...get(a, get(item, 'contentRealm.distance', []), []), item]), {});

        const sortedData = Object.keys(sectoredUsers).map((k) =>
          sectoredUsers[k]
            .sort((a, b) => {
              return get(a, 'name', '').localeCompare(get(b, 'name', ''));
            })
            .map((opt) => {
              return {
                ...opt,
                groupBy: `${get(opt, 'contentRealm.closestRealm')}${get(opt, 'contentRealm.belongsToUserRealm') === 0 ? ' - Other Companies' : ''}`,
              };
            })
            .sort((a, b) => {
              return get(a, 'groupBy', '').localeCompare(get(b, 'groupBy', ''));
            })
        );

        comp = (
          <FormControl variant="standard" className={classes.formControl}>
            <div className={!inputProps.disabled && classes.formWrap}>
              <AutoComplete
                classes={
                  inputProps.disabled
                    ? {
                        groupLabel: classes.primaryColor,
                        tag: classes.userSelectChip,
                        endAdornment: classes.collapsedScreen,
                      }
                    : {
                        groupLabel: classes.primaryColor,
                      }
                }
                variant="standard"
                multiple={get(inputProps, 'multiple', true)}
                options={flatten(sortedData)}
                groupBy={(opt) => opt.groupBy}
                getOptionLabel={(d) => {
                  const name = get(d, 'name', '');
                  if (isDisabled) {
                    const optIndex = ensureArray(value || []).findIndex((f) => f.id === get(d, 'id'));
                    const isLast = optIndex === size(ensureArray(value || [])) - 1;
                    return isLast ? name : `${name},`;
                  }
                  return name;
                }}
                getOptionKey={(d) => d.id}
                disableCloseOnSelect={get(inputProps, 'multiple', true)}
                onChange={(e, vals) => {
                  updateState(input.id, vals);
                }}
                value={value || []}
                renderInput={(params) => <TextField variant="standard" {...inputProps} onChange={noop} helperText={helperTextType} {...params} />}
                {...inputProps}
              />
            </div>
          </FormControl>
        );
      } else if (isType === 'TradeSelect') {
        comp = (
          <>
            {inputProps.label && (
              <InputLabel
                variant={"standard"}
                shrink={true}
                style={{ backgroundColor: 'transparent' }}
              >
                {inputProps.label}
              </InputLabel>
            )}
            <TradeSelect {...inputProps} value={value || []} onChange={(v) => updateState(input.id, v)} />
            {helperTextType && <FormHelperText error={inputProps.error}>{helperTextType}</FormHelperText>}
          </>
        );
      } else if (isType === 'Autocomplete') {
        const liveData = get(get(props, 'screens', []), [sIndex, 'inputs', index, 'data']);
        const inpData = liveData || input.data;
        const data = typeof inpData === 'function' ? inpData() : inpData;
        // If the default value is a string that matches one of the data options, we set it to that option
        //   This reduces the complexity of setting a default value by allowing it to be passed as a string
        //   but is particularly valuable when setting the default from a previously entered string value (on form refresh)
        if (typeof value === 'string' || value instanceof String) {
          flatten(data).every((d) => {
            if (get(d, 'name') === value) {
              value = d;
              return false;
            }
            return true;
          });
        }

        if (inputProps.filterType === 'customsearch' && !inputProps.filterOptions) {
          set(inputProps, 'filterOptions', (opts, s) => customSearch(opts, s, {}, true));
        }

        const onBlur = () => {
          let sel = value;

          let isPotentiallyNew = typeof sel === 'string' || sel instanceof String;
          if (isPotentiallyNew) {
            sel = sel.trim();

            const hasMatch = flatten(data).find((r) => {
              return r.name.toLowerCase() == sel.toLowerCase();
            });

            if (hasMatch) {
              sel = hasMatch;
            }
          }

          updateState(input.id, sel);
        };

        comp = (
          <FormControl variant="standard" className={classes.formControl}>
            <div className={!inputProps.disabled && classes.formWrap}>
              <AutoComplete
                classes={
                  inputProps.disabled
                    ? {
                        groupLabel: classes.primaryColor,
                        tag: classes.userSelectChip,
                        endAdornment: classes.collapsedScreen,
                      }
                    : {
                        groupLabel: classes.primaryColor,
                      }
                }
                variant="standard"
                multiple={get(inputProps, 'multiple', false)}
                options={flatten(data)}
                getOptionLabel={(d) => {
                  if (typeof d === 'string') return d;
                  const name = get(d, 'label', '') || get(d, 'name', '');
                  if (isDisabled) {
                    const vals = ensureArray(value || []);
                    const optIndex = vals.findIndex((f) => f.id === get(d, 'id'));
                    const isLast = size(vals) === 1 ? true : optIndex === size(value) - 1;
                    return isLast ? name : `${name},`;
                  }
                  return name;
                }}
                getOptionKey={(d) => d.id}
                onChange={(e, vals) => {
                  updateState(input.id, vals);
                  inputProps.onSelectOption && inputProps.onSelectOption(vals);
                }}
                onInputChange={(e, val) => {
                  const useInputChange = get(inputProps, 'useInputChange', true);
                  if (!useInputChange) {
                    return;
                  }
                  // if the newly typed string matches an option, set the option as the value
                  // This reduces unecessary new creation when a value is typed the same as one in the option list
                  if (typeof val === 'string' || val instanceof String) {
                    const valLower = val.toLowerCase();
                    if (
                      flatten(data).every((d) => {
                        if (get(d, 'name').toLowerCase() === valLower) {
                          updateState(input.id, d);
                          return false;
                        }
                        return true;
                      })
                    ) {
                      updateState(input.id, val);
                    }
                  } else {
                    updateState(input.id, val);
                  }
                }}
                freeSolo={true}
                includeInputInList={true}
                renderInput={(params) => <TextField variant="standard" {...inputProps} onBlur={noop} onChange={noop} helperText={helperTextType} {...params} />}
                defaultValue={value}
                onBlur={onBlur}
                {...inputProps}
              />
            </div>
          </FormControl>
        );
      } else if (isType === 'TextField') {
        comp = (
          <TextField
            variant={isDisabled ? "standard" : variant}
            onChange={(e) => updateState(input.id, e.target.value)}
            value={value}
            InputLabelProps={{ shrink: true }}
            classes={{
              root: classes.itemRoot,
            }}
            {...inputProps}
            helperText={helperTextType}
            minRows={!isDisabled && inputProps?.multiline ? inputProps?.minRows || 3 : undefined}
          />
        );
      } else if (isType === 'RichText') {
        if (isDisabled) {
          comp = (
            <FormControl disabled={true} className={clsx(classes.itemRoot, classes.block, 'MuiTextField-root')}>
              {!!get(inputProps, 'label') && (
                <InputLabel variant={"standard"} shrink={true} >
                  {get(inputProps, 'label')}
                </InputLabel>
              )}
              <div
                style={{ width: '100%', display: 'block' }}
                className={clsx('MuiInputBase-root MuiInput-root MuiInput-underline Mui-disabled Mui-disabled MuiInputBase-formControl MuiInput-formControl', classes.richTextDisabled)}
                dangerouslySetInnerHTML={{ __html: value || '<p></p>' }}
              />
            </FormControl>
          );
        } else {
          //RICH TEXT DOES NOT TRIGGER RERENDER (update=false) TO AVOID INFINITE LOOPING
          //BUT DOES NOTIFY PARENT (notify=true) SO CALLBACKS RECEIVE THE LATEST VALUE
          const upd = (e) => {
            const contentState = e.getCurrentContent();
            const html = draftToHtml(contentState);
            const isEmpty = !contentState.getPlainText().trim();
            updateState(input.id, isEmpty ? '' : html, false, true);
          };
          // Cache the htmlToDraft result per field so that re-renders don't produce
          // a new JSON string (convertFromHTML generates random block keys each time).
          // Without this, mui-rte's useEffect sees a "changed" defaultValue on every
          // render and resets the editor, making the field uneditable.
          if (!(input.id in richTextDefaultsRef.current)) {
            richTextDefaultsRef.current[input.id] = htmlToDraft(value);
          }
          const stableDefault = richTextDefaultsRef.current[input.id];
          const { label: richLabel, placeholder: richPlaceholder, error: _richError, ...richInputProps } = inputProps || {};
          comp = (
            <FormControl error={hasRequiredError} component="div">
              {!!richLabel && (
                <InputLabel variant={"standard"} shrink={true} style={{ position: 'relative', transform: 'none', fontSize: 12, marginBottom: 4 }}>
                  {richLabel}
                </InputLabel>
              )}
              <FormControl error={false} className={clsx(classes.formControl, classes.formWrapHover)} style={hasRequiredError ? { border: '1px solid #d32f2f', borderRadius: 4 } : undefined}>
              <div className={clsx('MuiFormControl-root MuiTextField-root', classes.itemRoot)}>
                <div className="RichText">
                  <RichText
                    defaultValue={stableDefault}
                    onChange={upd}
                    toolbarButtonSize="small"
                    controls={[
                      'title',
                      'bold',
                      'italic',
                      'underline',
                      'strikethrough',
                      'highlight',
                      'undo',
                      'redo',
                      'link',
                      'media',
                      'numberList',
                      'bulletList',
                      'quote',
                      'code',
                      'clear',
                      'save',
                      ...symbols.map((s) => s.name),
                    ]}
                    draftEditorProps={{
                      spellCheck: true,
                      onBlur: () => updateFS(formState),
                    }}
                    label={richPlaceholder || undefined}
                    customControls={symbols}
                    {...richInputProps}
                  />
                </div>
              </div>
            </FormControl>
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        }
      } else if (isType === 'KeyboardDatePicker') {
        const changeDate = (e) => {
          if (inputProps.convert) {
            e = inputProps.convert(e);
          }

          updateState(input.id, e);
        };
        comp = !isDisabled ? (
          <DatePicker
            variant={variant}
            autoOk={true}
            onChange={changeDate}
            classes={{
              root: classes.itemRoot,
            }}
            value={value || null}
            {...inputProps}
            slotProps={{
              textField: {
                error: get(inputProps, 'error', false),
                helperText: helperTextType,
              },
            }}
          />
        ) : (
          <TextField
            variant={"standard"}
            value={value ? moment(value).format('MM/DD/YYYY') : ''}
            {...inputProps}
          />
        );
      } else if (isType === 'ToggleButton') {
        comp = (
          <FormControl error={hasRequiredError} className={clsx(classes.toggleFormControl, center && classes.formControlCenter)}>
            {inputProps.label && <InputLabel variant={"standard"} shrink={true} className={center ? classes.labelCenter : undefined}>{inputProps.label}</InputLabel>}
            <ToggleButtonGroup
              classes={{
                root: classes.toggleBtnRoot,
              }}
              size="small"
              value={value}
              onChange={isDisabled ? undefined : (e, val) => val && updateState(input.id, val)}
              {...inputProps}
            >
              {input.options.map((opt, i) =>
                isDisabled ? (
                  opt.value === value ? (
                    <ToggleButton key={i} {...opt}>
                      {opt.title}
                    </ToggleButton>
                  ) : null
                ) : (
                  <ToggleButton key={i} {...opt}>
                    {opt.title}
                  </ToggleButton>
                )
              )}
            </ToggleButtonGroup>
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'DropZone') {
        comp = (
          <FormControl error={hasRequiredError} className={clsx(classes.formControl, 'dropZone')}>
            {inputProps.label && <InputLabel variant={"standard"} shrink={true}>{inputProps.label}</InputLabel>}
            {!isDisabled && <DropzoneArea onChange={(files) => updateState(input.id, files)} {...inputProps} showAlerts={['info', 'error']} />}
            {!!get(inputProps, 'initialFiles') && (
              <div className={clsx(classes.initialFiles, 'initialFiles')}>
                {get(inputProps, 'initialFiles', []).map((file, i) => {
                  if (get(inputProps, 'useChipsForPreview')) {
                    const renderChip = get(inputProps, 'renderChip');
                    if (renderChip) {
                      return renderChip({ key: i, file, inputProps });
                    }
                    return (
                      <Chip
                        key={i}
                        label={get(file, 'name')}
                        size="small"
                        onClick={() => {
                          window.open(file.fullUrl, '_blank');
                        }}
                        onDelete={isDisabled ? undefined : () => get(inputProps, 'onDeleteFile', noop)(file)}
                      />
                    );
                  }
                  return <Image src={file} useNaturalDims={false} key={i} alt={`image_${i}`} />;
                })}
              </div>
            )}
            {helperTextType && <FormHelperText style={{ marginTop: 15 }}>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'DropArea') {
        const stopProp = (e) => {
          e.stopPropagation();
          // e.preventDefault();
        };

        const deleteImage = (file, index) => {
          if (inputProps.confirmDelete) {
            const confirmed = window.confirm('Are you sure you want to remove this image?');
            if (!confirmed) {
              return;
            }
          }

          const photos = get(formState, input.id, []);
          photos.splice(index, 1);
          set(
            formState,
            input.id,
            photos.map((p) => ({ ...p, hasChanged: true }))
          );
          setFormState({ ...formState });
          updateState(input.id, get(formState, input.id));
        };

        const onSort = (order) => {
          order = (order || []).map((o) => ({ ...o, hasChanged: true }));
          updateState(input.id, order);
        };

        const onFilesChange = async (e) => {
          if (inputProps.uploadAllNew) {
            updateState(
              input.id,
              e.map((ee) => ({ ...ee, loading: true }))
            );
            e = await awaitAll(
              e.map((img) => () => async () => {
                if (img.file) {
                  const col = inputProps.bothMode
                    ? (isImgOrVid(img) ? 'task-images' : 'task-files')
                    : inputProps.collection;
                  const res = await uploadImageToPerform({
                    ...img,
                    collection: col,
                    custom_properties: {
                      caption: img.caption || get(img, 'custom_properties.caption', ''),
                      generated_conversions: {
                        large: true,
                        thumb: true,
                      },
                    },
                  });
                  return { ...res, ...get(res, 'attributes', {}) };
                }
                return img;
              }),
              {
                queueSize: 10,
              }
            );
          }
          if (inputProps.multiple === false) {
            updateState(
              input.id,
              [last(e)].filter((f) => !!f)
            );
          } else {
            updateState(input.id, e);
          }
        };

        const imageSelectSubmit = (e) => {
          const all = [
            ...get(formState, input.id, []),
            ...e.images.map((img) => ({
              ...pickFrom(img, [
                'assessment',
                'mediaId',
                'assessment_id',
                'collectionName',
                'createdAt',
                'custom_properties',
                'fullUrl',
                'largeUrl',
                'large-url',
                'id',
                'name',
                'quality_type',
                'realm',
                'realm_id',
                'realm_name',
                'realm_parent',
                'realm_tier',
                'scorecard_id',
                'scorecard_score_id',
                'superset',
                'superset_id',
                'task',
                'thumb-url',
                'thumbUrl',
                'updatedAt',
                'url',
                'user',
              ]),
              hasChanged: true,
            })),
          ];
          onFilesChange(all);
        };

        const accept = get(inputProps, 'accept', ['image/*', 'video/*']); //set accept to null to use this as not an image
        const isDocMode = !!inputProps.docMode;
        const isImageAccept = !isDocMode && (!accept || (Array.isArray(accept) ? accept : [accept]).some(a => a.includes('image') || a.includes('video')));
        const isBothMode = !!inputProps.bothMode;

        const onValuesChange = (o, baseLoc, index) => {
          if (o.archived) {
            deleteImage(o, index);
          } else if (o.newimg) {
            set(formState, baseLoc, { ...o.newimg, ...get(o, 'newimg.attributes', {}) });
            setFormState({ ...formState });
            updateState(input.id, formState[input.id]);
            setState(s => ({ ...s, mediaOpen: false }))
          } else {
            set(formState, `${baseLoc}.hasChanged`, true);
            const cap = get(o, 'values.custom_properties.caption', '');
            set(formState, `${baseLoc}.caption`, cap);
            setFormState({ ...formState });
            if (inputProps.shouldUpdateOnCaptionChange) {
              updateState(input.id, get(formState, input.id));
            }
          }
        };

        const imageSelect = get(inputProps, 'imageSelect');
        const showCaption = get(inputProps, 'showCaption', true);
        const _filesRaw = get(formState, input.id, []) || [];
        const files = Array.isArray(_filesRaw) ? [..._filesRaw] : [];

        if (isDisabled) {
          const RenderFile = get(inputProps, '_renderFile');
          comp = (
            <FormControl className={clsx(classes.formControl, 'dropArea')}>
              {inputProps.label && <InputLabel variant={"standard"} shrink={true}>{inputProps.label}</InputLabel>}
              <Box display="flex" flexWrap="wrap" className={inputProps.inputStyles || ''} style={{ ...(inputProps.label ? { marginTop: 20 } : {}), ...(size(files) === 0 ? { minHeight: 8 } : {}) }}>
                {RenderFile ? files.map((file, i) => (
                  <RenderFile key={i} file={file} deleteImage={() => {}} valueKey={`${input.id}.${i}`} formState={[formState, setFormState]} />
                )) : isBothMode ? (() => {
                  const { images: imgFiles, documents: docFiles } = splitMediaByType(files);
                  return (
                    <div style={{ width: '100%' }}>
                      {size(imgFiles) > 0 && <ImageList images={imgFiles} spaceBetween={inputProps.spaceBetween || 4} />}
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        {docFiles.map((file, i) => {
                          const fileName = get(file, 'name', get(file, 'fileName', 'Document'));
                          const url = get(file, 'fullUrl', get(file, 'url'));
                          return (
                            <div key={`doc-${i}`} style={{ margin: 4 }}>
                              <Chip icon={<Icon>description</Icon>} label={fileName} onClick={() => url && window.open(url, '_blank')} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })() : !isImageAccept ? files.map((file, i) => {
                  const fileName = get(file, 'name', get(file, 'fileName', 'Document'));
                  const url = get(file, 'fullUrl', get(file, 'url'));
                  return (
                    <div key={i} style={{ margin: 4 }}>
                      <Chip icon={<Icon>description</Icon>} label={fileName} onClick={() => url && window.open(url, '_blank')} />
                    </div>
                  );
                }) : (
                  <ImageList images={files} spaceBetween={inputProps.spaceBetween || 4} />
                )}
              </Box>
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        } else {
          comp = (
            <FormControl error={hasRequiredError} className={clsx(classes.formControl, 'dropArea')}>
              {inputProps.label && <InputLabel variant={"standard"} shrink={true} className={classes.formInputLabel}>{inputProps.label}</InputLabel>}
              <DropArea
                style={inputProps.label ? { marginTop: 20 } : undefined}
                accept={accept}
                docMode={isDocMode}
                onFilesChange={onFilesChange}
                files={files}
                onSort={onSort}
                className={clsx(classes.dropZone, hasRequiredError && classes.dropZoneError)}
                renderContent={() => <div style={{ padding: 8 }}>{isBothMode ? 'Drop or click to upload' : isImageAccept ? 'Drop images or click to upload' : 'Drop documents or click to upload'}</div>}
                renderFile={(file, key) => {
                  const RenderFile = get(inputProps, '_renderFile');
                  let i = get(formState, `${input.id}`, []).findIndex((f) => f.id == file.id);
                  if (i < 0) {
                    i = key;
                  }
                  const valueKey = `${input.id}.${i}`;
                  if (RenderFile && i !== -1) {
                    return <RenderFile file={file} key={key} deleteImage={() => deleteImage(file, i)} valueKey={valueKey} formState={[formState, setFormState]} />;
                  }

                  // In bothMode, check each file individually
                  const fileIsImage = isBothMode ? isImgOrVid(file) : isImageAccept;

                  if (!fileIsImage) {
                    return (
                      <div key={`${input.id}.${i}`} onClick={stopProp}>
                        <Chip
                          label={get(file, 'file.name', get(file, 'name', get(file, 'fileName', '')))}
                          onDelete={() => deleteImage(file, i)}
                          style={{ margin: 2 }}
                        />
                      </div>
                    );
                  }

                  const baseLoc = `${input.id}.${i}`;
                  const loc = `${baseLoc}.caption`;
                  const canDelete = get(file, 'canDelete', true);
                  const isLoading = inputProps.uploadAllNew && !!files.find((f) => `${f.id}`.includes(FILEID));

                  return (
                    <div key={loc} className={classes.imageWrap} onClick={stopProp}>
                      {(accept && accept.includes('image') > -1 || isBothMode) && (
                        <Media
                          {...inputProps}
                          {...file}
                          loading={isLoading}
                          images={files.filter((f) => isBothMode ? isImgOrVid(f) : true)}
                          index={isBothMode ? files.filter((f) => isImgOrVid(f)).findIndex((f) => f.id == file.id) : i}
                          onValuesChange={(o) => onValuesChange(o, baseLoc, i)}
                          childImagesIncludedInEdit
                          onOpen={() => setState((s) => ({ ...s, mediaOpen: true }))}
                          onClose={() => setState((s) => ({ ...s, mediaOpen: false }))}
                        />
                      )}
                      {showCaption && !isLoading && !state.mediaOpen && (
                        <TextField
                          multiline
                          minRows={2}
                          variant="standard"
                          placeholder="caption"
                          // value={inputProps.shouldUpdateOnCaptionChange ? get(formState, loc) : undefined} //we are removing this because of this issue https://ibacos.atlassian.net/browse/PFM-1995
                          defaultValue={get(formState, loc)}
                          onChange={(e) => {
                            set(formState, `${baseLoc}.hasChanged`, true);
                            set(formState, loc, e.target.value);
                            setFormState({ ...formState });
                            if (inputProps.shouldUpdateOnCaptionChange) {
                              updateState(input.id, get(formState, input.id));
                            }
                          }}
                          className={classes.comment}
                          InputProps={{
                            className: 'no-drag',
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  variant="none"
                                  toolTip={!canDelete ? "This media is a reference and can't be edited." : undefined}
                                  disabled={!canDelete}
                                  className={classes.imageDelete}
                                  onClick={() => deleteImage(file, i)}
                                  size="large"
                                >
                                  <Icon>close</Icon>
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </div>
                  );
                }}
                {...inputProps}
              />
              {!!imageSelect && (
                <div className={'dropArea ImageSelect'}>
                  <ImageSelectBtn {...imageSelect} onSubmit={imageSelectSubmit} />
                </div>
              )}
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        }
      } else if (isType === 'RadioButton') {
        if (isDisabled) {
          comp = (
            <>
              <TextField
                variant={variant}
                value={get(
                  input.options.find((opt) => get(opt, 'value') === value),
                  'label',
                  value
                )}
                classes={{
                  root: classes.itemRoot,
                }}
                {...inputProps}
              />
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </>
          );
        } else {
          comp = (
            <FormControl error={hasRequiredError} className={classes.toggleFormControl}>
              {inputProps.label && (
                <InputLabel shrink={true} variant="standard">
                  {inputProps.label}
                </InputLabel>
              )}
              <RadioGroup
                classes={{
                  root: classes.toggleListRoot,
                }}
                size="small"
                value={value}
                onChange={isDisabled ? undefined : (e, val) => val && updateState(input.id, val)}
                {...inputProps}
              >
                {input.options.map((opt, i) => (
                  <FormControlLabel key={i} {...opt} control={<Radio />} />
                ))}
              </RadioGroup>
              {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
            </FormControl>
          );
        }
      } else if (isType === 'Checkbox') {
        comp = (
          <FormControl error={hasRequiredError} className={classes.toggleFormControl}>
            {inputProps.label && (
              <InputLabel shrink={false} className={classes.ourLabel}>
                {inputProps.label}
                {!!inputProps.tooltip && (
                  <Tooltip title={inputProps.tooltip.title}>
                    <Icon className={classes.infoIcon} color="primary">
                      info_circle
                    </Icon>
                  </Tooltip>
                )}
              </InputLabel>
            )}
            <FormGroup
              classes={{
                root: classes.toggleListRoot,
              }}
              size="small"
              {...inputProps}
            >
              {(isDisabled && !inputProps.showAll ? input.options.filter((o) => get(value, o.value)) : input.options).map((opt, i) => (
                <FormControlLabel
                  key={i}
                  {...opt}
                  control={
                    <Checkbox
                      onChange={isDisabled ? undefined : (e) => updateState(input.id, { ...value, [opt.value]: e.target.checked })}
                      disabled={isDisabled}
                      checked={isDisabled && !inputProps.showAll ? true : !!get(value, opt.value)}
                    />
                  }
                />
              ))}
            </FormGroup>
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'ColorSquare') {
        const color = `#${value}`;
        const isValid = isValidHex(color);
        const showColorPicker = !!get(subState, input.id);
        const onColorChange = (c, e) => {
          e.stopPropagation();
          e.preventDefault();
          updateState(input.id, c.hex.replace('#', ''));
        };
        const toggleColorPicker = () => {
          if (isDisabled) {
            return;
          }
          setSubState({ ...subState, [input.id]: !showColorPicker });
        };
        comp = (
          <TextField
            onClick={!showColorPicker && toggleColorPicker}
            variant={variant}
            error={!isValid}
            helperText={!isValid && `Invalid hex color`}
            onChange={(e) => updateState(input.id, e.target.value)}
            value={value}
            InputProps={{
              startAdornment: <InputAdornment position="start">#</InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <div onClick={toggleColorPicker} className={classes.squareColor} style={{ backgroundColor: `#${value}` }} />
                  {showColorPicker && <div className={classes.colorPickerOverlay} onClick={toggleColorPicker} />}
                  {showColorPicker && (
                    <div className={classes.colorPicker}>
                      <ChromePicker color={color} disableAlpha={true} onChangeComplete={onColorChange} />
                    </div>
                  )}
                </InputAdornment>
              ),
            }}
            classes={{
              root: classes.itemRoot,
            }}
            {...inputProps}
          />
        );
      } else if (isType === 'ColorSlider') {
        const onColorChange = (e, v) => {
          if (isDisabled) {
            return;
          }
          e.stopPropagation();
          e.preventDefault();
          updateState(input.id, v);
        };
        comp = (
          <FormControl className={classes.toggleFormControl}>
            {inputProps.label && (
              <InputLabel shrink={true} variant="standard">
                {inputProps.label}
              </InputLabel>
            )}
            <div className={classes.colorSliderWrap}>
              <ColorSlider {...inputProps} disabled={false} value={value} onChange={onColorChange} />
            </div>
          </FormControl>
        );
      } else if (isType === 'ChipAdd') {
        const maxItems = get(inputProps, 'maxItems');
        const atMax = maxItems != null && Array.isArray(value) && value.length >= maxItems;
        const fieldSubmit = () => {
          if (atMax) return;
          const fieldVal = get(subState, input.id);
          if (!fieldVal) {
            return;
          }
          updateState(input.id, [...value, fieldVal]);
          setSubState({ ...subState, [input.id]: '' });
        };
        comp = (
          <FormControl className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && <InputLabel variant={"standard"} shrink={true}>{inputProps.label}</InputLabel>}
            <div className={classes.chipAddChipContainer}>
              {size(value) === 0 && isDisabled && <Typography>None</Typography>}
              {(value || []).map((v, i) => {
                return (
                  <Chip
                    size="small"
                    key={i}
                    label={v}
                    onDelete={
                      isDisabled
                        ? undefined
                        : () => {
                            value.splice(i, 1);
                            updateState(input.id, value);
                          }
                    }
                    {...get(input, 'chipProps', {})}
                  />
                );
              })}
            </div>
            {!isDisabled && (
              <div className={classes.chipAddInputContainer}>
                <TextField
                  variant={variant}
                  size="small"
                  disabled={atMax}
                  helperText={atMax ? `Maximum of ${maxItems} items allowed` : undefined}
                  onChange={(e) => setSubState({ ...subState, [input.id]: e.target.value })}
                  value={atMax ? '' : get(subState, input.id)}
                  onKeyDown={(e) => {
                    if (e.keyCode == 13) {
                      fieldSubmit();
                    }
                  }}
                  InputProps={{
                    placeholder: get(inputProps, 'placeholder'),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={fieldSubmit} disabled={atMax} className={classes.chipAddBtn}>
                          <Icon>add_circle</Icon>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  classes={{
                    root: classes.itemRoot,
                  }}
                />
              </div>
            )}
          </FormControl>
        );
      } else if (isType === 'ChipSelect') {
        const chipData = input.data || [];
        const allowCustomInput = !!input.allowCustomInput;
        const allowMultiple = input.allowMultiple !== undefined ? input.allowMultiple : true;
        const valArr = Array.isArray(value) ? value : (value ? [value] : []);

        // Build option objects from chipData for the Autocomplete
        const allOptions = chipData.map((opt) => ({
          label: opt.label || opt.value || opt,
          value: opt.value || opt.label || opt,
        }));

        // Filter out already-selected options from the dropdown
        // In single-select mode, show all options so the user can pick a replacement
        const availableOptions = allowMultiple
          ? allOptions.filter((o) => !valArr.includes(o.value))
          : allOptions;

        const removeValue = (val) => {
          updateState(input.id, valArr.filter((v) => v !== val));
        };

        comp = (
          <FormControl error={hasRequiredError} component="div" className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && <InputLabel variant="standard" shrink style={{ fontSize: 12 }}>{inputProps.label}</InputLabel>}
            {!isDisabled && (
              <MuiAutocomplete
                multiple={allowMultiple}
                freeSolo={allowCustomInput}
                blurOnSelect={!allowMultiple}
                options={availableOptions}
                value={allowMultiple ? [] : null}
                getOptionLabel={(opt) => {
                  if (typeof opt === 'string') return opt;
                  return opt.label || opt.value || '';
                }}
                isOptionEqualToValue={() => false}
                onChange={(e, newVal) => {
                  if (allowMultiple) {
                    // newVal contains only the newly picked item(s) since value is always []
                    const added = (Array.isArray(newVal) ? newVal : [newVal]).filter(Boolean);
                    const newVals = added.map((v) => (typeof v === 'string' ? v : v.value));
                    updateState(input.id, [...valArr, ...newVals]);
                  } else {
                    const picked = typeof newVal === 'string' ? newVal : get(newVal, 'value');
                    updateState(input.id, picked ? [picked] : []);
                  }
                }}
                renderTags={() => null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant={variant}
                    size="small"
                    placeholder={get(inputProps, 'placeholder', 'Select...')}
                    error={get(inputProps, 'error', false)}
                  />
                )}
                classes={{ root: classes.itemRoot }}
              />
            )}
            {valArr.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {valArr.map((v) => {
                  const opt = allOptions.find((o) => o.value === v);
                  const label = opt ? opt.label : v;
                  return (
                    <Chip
                      size="small"
                      key={v}
                      label={label}
                      onDelete={isDisabled ? undefined : () => removeValue(v)}
                    />
                  );
                })}
              </div>
            )}
            {helperTextType && <FormHelperText>{helperTextType}</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'Repeater') {
        const { nestedInputs } = input;
        const itemName = get(inputProps, 'itemName') || get(inputProps, 'label') || 'Item';

        const handleItemChange = (idx, vals) => {
          // Read latest items from formState (mutated in place) to avoid stale closures
          const currentItems = [...(get(formState, input.id) || [])];
          const existingKey = get(currentItems, [idx, '_key']);
          // Extract values using lodash get (follows dot-path) but assign with literal keys
          // to avoid nested objects when field labels contain periods
          const item = {};
          nestedInputs.forEach((ni) => {
            const v = get(vals, ni.id);
            if (v !== undefined) item[ni.id] = v;
          });
          if (existingKey) item._key = existingKey;
          currentItems[idx] = item;
          updateState(input.id, currentItems);
        };

        const addItem = () => {
          updateState(input.id, [...(value || []), { _key: Date.now() }]);
        };

        const removeItem = (idx) => {
          const items = [...(value || [])];
          items.splice(idx, 1);
          updateState(input.id, items);
        };

        comp = (
          <FormControl error={hasRequiredError} className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && (
              <InputLabel shrink={false} className={classes.ourLabel}>
                {inputProps.label}
              </InputLabel>
            )}
            {_helperText && (
              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8 }}>
                {typeof _helperText === 'function' ? _helperText(formState) : _helperText}
              </Typography>
            )}
            {(value || []).map((item, idx) => (
              <div key={item._key || idx} style={{ marginBottom: 16, paddingLeft: 16, borderLeft: '3px solid #ddd' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    {itemName} #{idx + 1}
                  </Typography>
                  {!isDisabled && (
                    <IconButton size="small" onClick={() => removeItem(idx)}>
                      <Icon>close</Icon>
                    </IconButton>
                  )}
                </div>
                <RepeaterItem
                  FormComponent={Form}
                  item={item}
                  itemIndex={idx}
                  nestedInputs={nestedInputs}
                  disabled={isDisabled}
                  onItemChange={handleItemChange}
                  itemName={itemName}
                  formStyle={classes.formContainerFullWidth}
                />
              </div>
            ))}
            {!isDisabled && (
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginTop: 4, gap: 4 }}
                onClick={addItem}
              >
                <Typography variant="body2">Add Another</Typography>
                <Icon fontSize="small">add_circle</Icon>
              </div>
            )}
            {hasRequiredError && <FormHelperText>This field is required</FormHelperText>}
          </FormControl>
        );
      } else if (isType === 'JsonBuilder') {
        const updateFieldValue = (i, key, val) => {
          set(value, `${i}.${key}`, val);
          updateState(input.id, value);
        };

        const { options } = input;

        const color = 'secondary';

        const renderValueField = (v, i) => {
          const selproperty = get(v, 'property', '');
          const currentOpts = get(
            options.find((o) => o.id === selproperty),
            'props',
            get(input, 'defaultProps', {})
          );

          const val = get(value, [i, 'value']);

          const valueIsValid = (() => {
            let valid = val !== undefined && val !== '';
            const type = get(currentOpts, 'type', 'string');

            if (currentOpts.isValid) {
              valid = currentOpts.isValid(val);
            } else {
              if (type === 'duration') {
                const regex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/gm;
                const matches = (val || '').match(regex);
                valid = !!size(matches);
              }

              if (type === 'number') {
                valid = !isNaN(parseInt(val));
              }

              if (type === 'string' && currentOpts.blankAllowed) {
                valid = true;
              }
            }

            return valid;
          })();

          const valObj = get(value, i);
          if (get(valObj, 'valid') !== valueIsValid) {
            updateFieldValue(i, 'valid', valueIsValid);
          }

          const onChange = (e) => {
            updateFieldValue(i, 'value', e.target.value);
            updateFieldValue(i, 'isDirty', true);
          };

          const helperText = !valueIsValid ? currentOpts.invalidText || 'Invalid format' : undefined;

          const standard = {
            ...currentOpts,
            variant: isDisabled ? 'standard' : 'outlined',
            size: 'small',
            disabled: isDisabled,
            error: !valueIsValid,
            helperText,
            onChange,
            value: val,
            style: { flex: 1 },
          };

          if (!currentOpts.type || currentOpts.type === 'number') {
            return <TextField {...standard} />;
          } else if (currentOpts.type === 'Select') {
            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Select {...standard}>
                  {currentOpts.options.map((o) => (
                    <MenuItem value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                {!!standard.error && <FormHelperText error>{standard.helperText}</FormHelperText>}
              </div>
            );
          } else if (currentOpts.type === 'RichText') {
            const richUpd = (e) => {
              const html = draftToHtml(e.getCurrentContent());
              set(value, `${i}.value`, html);
              set(value, `${i}.isDirty`, true);
              updateState(input.id, value, false, false);
            };
            return (
              <div className={clsx('RichText', classes.itemRoot)} style={{ width: '100%', marginTop: 8 }}>
                <RichText
                  defaultValue={htmlToDraft(val || '')}
                  onChange={richUpd}
                  readOnly={isDisabled}
                  toolbarButtonSize="small"
                  controls={isDisabled ? [] : ['bold', 'italic', 'underline', 'link', 'numberList', 'bulletList', 'clear']}
                  draftEditorProps={{ spellCheck: true }}
                />
              </div>
            );
          } else if (currentOpts.type === 'bool') {
            return (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Select {...standard}>
                  <MenuItem value={false}>False</MenuItem>
                  <MenuItem value={true}>True</MenuItem>
                </Select>
                {!!standard.error && <FormHelperText error>{standard.helperText}</FormHelperText>}
              </div>
            );
          }

          return <TextField {...standard} />;
        };

        comp = (
          <FormControl className={clsx(classes.toggleFormControl, inputProps.className)}>
            {inputProps.label && (
              <InputLabel shrink={false} className={classes.ourLabel}>
                {inputProps.label}
                {!!inputProps.tooltip && (
                  <Tooltip title={inputProps.tooltip.title}>
                    <Icon className={classes.infoIcon} color="primary">
                      info_circle
                    </Icon>
                  </Tooltip>
                )}
              </InputLabel>
            )}
            {(value || []).map((val, i) => {
              const optProps = options.find((o) => o.id == val.property);
              const isRichText = get(optProps, 'props.type') === 'RichText';

              return !get(optProps, 'show', true) ? null : (
                <div key={val.property} style={{ border: isDisabled ? 'none' : '1px solid #ddd', borderRadius: 4, padding: isDisabled ? '4px 0' : 12, marginBottom: isDisabled ? 4 : 12 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {!!get(optProps, 'canDelete', true) && !isDisabled && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          value.splice(i, 1);
                          updateState(input, value);
                        }}
                        style={{ color: '#1976d2', marginTop: 8 }}
                      >
                        <Icon>close</Icon>
                      </IconButton>
                    )}
                    <FormControl variant={isDisabled ? 'standard' : 'outlined'} size="small" style={{ minWidth: 160 }}>
                      {!isDisabled && <InputLabel>Select Option</InputLabel>}
                      <Select disabled={isDisabled} label={isDisabled ? undefined : 'Select Option'} value={val.property} onChange={(e) => updateFieldValue(i, 'property', e.target.value)}>
                        {options.map((opt, ii) => {
                          const hasKey = !!value.find((v) => v.property == opt.id);
                          return (
                            <MenuItem disabled={hasKey} key={opt.id} value={opt.id}>
                              {opt.label}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    {!isRichText && renderValueField(val, i)}
                  </div>
                  {isRichText && renderValueField(val, i)}
                </div>
              );
            })}
            {!isDisabled && (
              <Button {...get(input, 'addBtn', {})} style={{ marginTop: 4 }} onClick={() => updateState(input.id, [...(value || []), {}])} startIcon={<Icon>edit</Icon>}>
                {get(input, 'addBtn.title', 'ADD MORE')}
              </Button>
            )}
          </FormControl>
        );
      } else {
        console.error('COMPONENT TYPE NOT SETUP!!');
      }
    } else if (input.component) {
      if (inputProps.label) {
        comp = (
          <FormControl className={classes.toggleFormControl}>
            <InputLabel disableAnimation={true} shrink={true} variant={"standard"}>{inputProps.label}</InputLabel>
            {typeof input.component == 'function' ? input.component() : input.component}
          </FormControl>
        );
      } else {
        comp = typeof input.component === 'function' ? input.component() : input.component;
      }
    }

    const style = get(input, 'style', {});

    //return render
    const canAdd = typeof input.add === 'function' ? input.add(formState, input) : input.add;
    const canRemove = typeof input.remove === 'function' ? input.remove(formState, input) : input.remove;
    let buttonRow = null;

    if (canRemove || canAdd) {
      buttonRow = (
        <div className={classes.row} style={{ justifyContent: 'flex-start' }}>
          {canAdd && (
            <Button className={classes.addBtn} variant="contained" onClick={() => addAnotherInput(input, index)} startIcon={<Icon>add</Icon>}>
              <Typography>{get(input, 'addBtn.title', 'Add')}</Typography>
            </Button>
          )}

          {canRemove && (
            <Button className={clsx(classes.addBtn, classes.removeBtn)} variant="contained" onClick={() => removeInput(input, index)} startIcon={<Icon>delete_forever</Icon>}>
              {!!get(input, 'deleteBtn.title') && <Typography>{get(input, 'deleteBtn.title')}</Typography>}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div key={input.id} className={clsx(classes.inputEach, input.inputStyle, 'inputEach', isDisabled && classes.inputDisabled, isLast && isDisabled && classes.inputDisabledLast)} style={style}>
        {comp}
        {buttonRow}
      </div>
    );
  };

  const [requiredErrors, setRequiredErrors] = useState([]);

  const validateRequired = () => {
    const allowed = canContinue(true);
    setRequiredErrors(allowed.passes ? [] : allowed.failed.map((f) => f.id));
    return allowed;
  };

  const submit = async () => {
    const allowed = validateRequired();
    if (allowed.passes) {
      onSubmit && (await onSubmit(formState));
    }
  };

  const canContinue = (isSubmit = false) => {
    const _screens = latestRef.current.screens;
    const _screenIndex = latestRef.current.screenIndex;
    const _formState = latestRef.current.formState;

    if (!isSubmit && _screenIndex === _screens.length - 1) {
      return { passes: false, failed: [] };
    }

    const screenInputs = get(_screens, [_screenIndex, 'inputs'], []);
    let failed = [];
    let passes = true;
    for (let i = 0; i < screenInputs.length; i++) {
      const input = screenInputs[i];
      const inputVal = get(_formState, input.id);
      const isRequired = input.required || get(input, 'props.required');
      if (isRequired && (inputVal === undefined || inputVal === null || inputVal === '' || (isObject(inputVal) && !(inputVal instanceof Date) && _size(inputVal) === 0))) {
        passes = false;
        failed.push(input);
      }
    }
    return { passes, failed };
  };

  const renderScreen = (screen, sIndex = 0) => {
    const title = get(screen, 'title');
    const description = get(screen, 'description');

    const titleStyle = get(screen, 'titleStyle');

    const collapsable = get(screen, 'collapsable');
    const colp = get(collapsed, sIndex);

    const CONT = get(screen, 'screenContainer.component', 'div');
    const CONT_PROPS = get(screen, 'screenContainer.props', {});

    const screenContainerStyle = get(screen, 'screenContainerStyle');

    const isFullScreenForm = get(screen, 'fullScreenView', false);

    const onSort = (order, event) => {
      screen.onSort && screen.onSort(order, event);
    };

    return (
      <CONT key={sIndex} {...CONT_PROPS} className={clsx(isFullScreenForm ? classes.screenSubContainerDialog : classes.screenSubContainer, get(CONT_PROPS, 'className', 'screenSubContainer'))}>
        {title && (
          <div className={clsx('titleContainer', classes.titleContainer)}>
            <div className={classes.titleRow}>
              <Text type={'title_large'} className={titleStyle}>
                {title}
              </Text>

              {collapsable && (
                <Button
                  className={classes.collapseButton}
                  size="small"
                  variant="default"
                  color="secondary"
                  endIcon={<Icon>{colp ? 'arrow_drop_down' : 'arrow_drop_up'}</Icon>}
                  onClick={() =>
                    setCollapsed({
                      ...set(collapsed, sIndex, !colp),
                    })
                  }
                  title={colp ? 'Show' : 'hide'}
                />
              )}
            </div>
            {description && <Typography variant="inherit">{description}</Typography>}
          </div>
        )}

        {screen && (
          <div className={clsx(classes.screenContainer, screenContainerStyle, colp && classes.collapsedScreen)}>
            {screen.sortable ? (
              <DragSortableList
                items={screen.inputs.map((input, index) => ({ content: getInputType(screen, input, index, sIndex), key: input.id }))}
                dropBackTransitionDuration={0.3}
                onSort={onSort}
                placeholder={<div className={`placeholderContent`}>DROP HERE</div>}
                type={'vertical'}
              />
            ) : (
              screen.inputs.map((input, index) => {
                return getInputType(screen, input, index, sIndex);
              })
            )}
          </div>
        )}
      </CONT>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      updateState,
      clearState,
      formState,
      validateRequired,
      canContinue,
    };
  }, []);

  return (
    <div style={{ height: '100%' }} ref={formref}>
      {loading ? (
        <div className={classes.formContainer}>
          <Box className={classes.centerColumn} style={{ padding: 20, margin: 'auto' }}>
            <Typography variant={'h4'}>{get(props, 'loadingMessage', 'Please Wait...')}</Typography>
            <LoadingTools fontSize="large" color="primary" style={{ marginTop: 10, fontSize: '6rem' }} />
          </Box>
        </div>
      ) : (
        <div className={clsx(classes.formScrollContainer, 'formScrollContainer')}>
          <div className={isNoMarginContainer ? clsx(classes.formContainerNoMargin, get(props, 'formStyle')) : clsx(classes.formContainer, get(props, 'formStyle'))}>
            {showAllScreens
              ? screens.map((screen, i) => {
                  if (Array.isArray(screen)) {
                    return (
                      <div key={i} className={clsx(classes.screenWrapper, 'screenWrapper', `screenWrapper_${i}`)}>
                        {screen.map((scr, _i) => renderScreen(scr, `${i}.${_i}`))}
                      </div>
                    );
                  }
                  return <div className={clsx(classes.screenWrapper, 'screenWrapper')}>{renderScreen(screen, i)}</div>;
                })
              : renderScreen(screens[screenIndex], screenIndex)}
          </div>
        </div>
      )}

      {!loading && (showPosition || showNavigation || canSubmit) && (
        <Toolbar position="fixed" style={{ flexDirection: 'column', justifyContent: 'space-around', alignItems: 'center' }}>
          <Box>
            {showPosition && (
              <div className={classes.positionContainer}>
                {screens.map((screen, i) => {
                  const num = i + 1;
                  const posTitle = get(screen, 'positionTitle', get(screen, 'title', ''));
                  const isActive = i <= screenIndex;
                  const showBar = screens.length > 1 && num < screens.length;
                  const barActive = i < screenIndex;
                  return (
                    <Fragment key={i}>
                      <div className={classes.positionWrap}>
                        <div className={clsx(classes.posNumber, isActive && classes.backgroundPrimary)}>{num}</div>
                        <div className={clsx(classes.posTitle, isActive && classes.colorPrimary)}>{posTitle}</div>
                      </div>
                      {showBar && <div className={clsx(classes.bar, barActive && classes.colorPrimary)} />}
                    </Fragment>
                  );
                })}
              </div>
            )}

            {showNavigation && (
              <div className={classes.buttonsContainer}>
                <Button color="primary" variant="outlined" disabled={screenIndex === 0} className={classes.button} onClick={() => setScreenIndex(screenIndex - 1)}>
                  BACK
                </Button>
                <Button color="primary" variant="contained" disabled={!canContinue().passes} className={classes.button} onClick={() => setScreenIndex(screenIndex + 1)}>
                  NEXT
                </Button>
                <Button color="primary" variant="contained" disabled={screenIndex !== screens.length - 1 || !canContinue(true).passes} className={classes.buttonFinish} onClick={submit}>
                  FINISH
                </Button>
              </div>
            )}
          </Box>
          <Box style={{ position: 'fixed', bottom: '8px', zIndex: 100, ...(props.submitBtnBoxStyle || {}) }}>
            {canSubmit && (
              <div className={classes.buttonsContainer}>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={props.useCheckContinue ? !canContinue(true).passes : false}
                  className={classes.buttonFinish}
                  style={!props.useCheckContinue && !canContinue(true).passes ? { opacity: 0.5 } : undefined}
                  startIcon={get(props, 'submitIcon')}
                  onClick={submit}
                >
                  {get(props, 'submitText', 'SUBMIT')}
                </Button>
              </div>
            )}
          </Box>
        </Toolbar>
      )}
    </div>
  );
});

export default Form;
